import Avatar from "@components/Avatar/Avatar";
import Switcher from "@components/Switch/Switcher";
import Footer from "@components/layout/Footer";
import Loading from "@components/layout/Loading";
import { auth, db } from "@utils/firebase";
import { useGetUser } from "@utils/hooks";
import { Timestamp, collection, doc, setDoc } from "firebase/firestore";
import React, { Suspense, useMemo, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Await } from "react-router-dom";

type ProfileManageProps = {};

interface FormValues {
  email_information: boolean;
  email_marketing: boolean;
  email_products: boolean;

  phone_information: boolean;
  phone_marketing: boolean;
  phone_products: boolean;

  letter_information: boolean;
  letter_marketing: boolean;
  letter_products: boolean;
}

const ProfileManage: React.FC<ProfileManageProps> = () => {
  const [user, loadingUser] = useAuthState(auth);
  const { data, isLoading } = useGetUser();

  const getUserPromise = useMemo(() => {
    return new Promise((resolve) => {
      if (!loadingUser && !isLoading) {
        resolve({
          nationality: data?.nationality ?? "-",
          birth_date: data?.birth_date ?? "-",
          email: user?.email ?? "-",
          name:
            user?.displayName ??
            (data?.first_name && data?.last_name 
              ? `${data.first_name} ${data.last_name}`
              : "-"),
          phone: user?.providerData[0].phoneNumber ?? data?.phone_number ?? "-",
          memberSince: data?.createdAt 
            ? new Timestamp(data.createdAt.seconds, data.createdAt.nanoseconds)
                .toDate()
                .getFullYear()
            : new Date().getFullYear(),
        });
      }
    });
  }, [loadingUser, isLoading, data, user]);

  const initialValues = useMemo(() => {
    return {
      email_information: data?.email_information ?? false,
      email_marketing: data?.email_marketing ?? false,
      email_products: data?.email_products ?? false,
      phone_information: data?.phone_information ?? false,
      phone_marketing: data?.phone_marketing ?? false,
      phone_products: data?.phone_products ?? false,
      letter_information: data?.letter_information ?? false,
      letter_marketing: data?.letter_marketing ?? false,
      letter_products: data?.letter_products ?? false,
    };
  }, [data]);

  const [toggles, setToggles] = useState<FormValues>(initialValues);

  const updateDocs = async (name: string, value: boolean) => {
    const user_ref = doc(collection(db, "users"), user?.uid || "a");

    return await setDoc(
      user_ref,
      {
        [name]: value,
      },
      { merge: true }
    );
  };

  const handleToggles = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToggles((prevData) => {
      updateDocs(
        e.target.name,
        !prevData[e.target.name as keyof typeof prevData]
      );

      return {
        ...prevData,
        [e.target.name]: !prevData[e.target.name as keyof typeof prevData],
      };
    });
  };

  return (
    <Suspense fallback={<Loading />}>
      <section className="px-4 py-0 md:pb-24 md:pt-10 lg:py-10 lg:px-[80px] w-full flex flex-col items-stretch gap-y-0 lg:gap-x-[23px]">
        <Await resolve={getUserPromise}>
          {(resolvedUserData: { name: string; memberSince: string }) => (
            <div className="hidden md:block w-full pb-6">
              <div className="flex items-end gap-x-4">
                <div>
                  <Avatar size={100} user={user!} />
                </div>

                <div className="flex flex-col gap-y-1">
                  <span className="text-pn-large-title text-pn_gray-950">
                    {resolvedUserData.name}
                  </span>
                  <span className="text-pn-body-large-regular text-pn_gray-950">
                    Penny Member since {resolvedUserData.memberSince}
                  </span>
                </div>
              </div>
            </div>
          )}
        </Await>

        <div className="py-4 block md:hidden">
          <span className="text-pn-large-title">Your profile</span>
        </div>

        <div className="flex flex-col md:flex-row gap-x-0 md:gap-x-[23px] gap-y-4 md:gap-y-0 w-full items-start md:items-stretch">
          <div className="flex-1 flex flex-col w-full">
            <div className="py-4 flex items-center justify-between">
              <span className="text-pn-body-large-emphasis text-pn_gray-950">
                Personal details
              </span>
            </div>

            <Await resolve={getUserPromise}>
              {(resolvedUserData: {
                phone: string;
                name: string;
                email: string;
                birth_date: string;
                nationality: string;
              }) => (
                <div className="p-6 flex-1 bg-pn_white border border-pn_gray-200 rounded-2xl">
                  <div className="flex flex-col gap-y-4">
                    <div className="flex items-center justify-between gap-x-4">
                      <div className="flex flex-col gap-y-2 flex-1">
                        <span className="text-pn-callout-emphasis text-pn_gray-950">
                          Full Name
                        </span>
                        <span className="text-pn-body-regular text-pn_gray-950">
                          {resolvedUserData.name}
                        </span>
                      </div>
                      <div className="block md:hidden">
                        <Avatar size="large" user={user!} />
                      </div>
                    </div>

                    <div className="flex flex-col gap-y-2 flex-1">
                      <span className="text-pn-callout-emphasis text-pn_gray-950">
                        Email Address
                      </span>
                      <span className="text-pn-body-regular text-pn_gray-950">
                        {resolvedUserData.email}
                      </span>
                    </div>

                    <div className="flex flex-col gap-y-2 flex-1">
                      <span className="text-pn-callout-emphasis text-pn_gray-950">
                        Date of Birth
                      </span>
                      <span className="text-pn-body-regular text-pn_gray-950">
                        {resolvedUserData.birth_date}
                      </span>
                    </div>

                    <div className="flex flex-col gap-y-2 flex-1">
                      <span className="text-pn-callout-emphasis text-pn_gray-950">
                        Nationality
                      </span>
                      <span className="text-pn-body-regular text-pn_gray-950">
                        {resolvedUserData.nationality}
                      </span>
                    </div>

                    <div className="flex flex-col gap-y-2 flex-1">
                      <span className="text-pn-callout-emphasis text-pn_gray-950">
                        Phone Number
                      </span>
                      <span className="text-pn-body-regular text-pn_gray-950">
                        {resolvedUserData.phone}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </Await>
          </div>

          <div className="flex-1 flex flex-col w-full">
            <div className="py-4">
              <span className="text-pn-body-large-emphasis text-pn_gray-950">
                Communication Preferences
              </span>
            </div>
            <div className="p-6 flex-1 bg-pn_white border border-pn_gray-200 rounded-2xl">
              <div className="flex flex-col gap-y-4">
                <div className="flex flex-col gap-y-[7px]">
                  <span className="text-pn-callout-emphasis text-pn_gray-950">
                    Account Information
                  </span>
                  <div className="flex flex-col gap-y-[14px]">
                    <div className="flex items-center gap-x-2">
                      <span className="text-pn-body-regular text-pn_gray-950 flex-1">
                        Email
                      </span>
                      <Switcher
                        onChange={handleToggles}
                        checked={toggles.email_information}
                        name="email_information"
                        id="email_information"
                      />
                    </div>

                    <div className="flex items-center gap-x-2">
                      <span className="text-pn-body-regular text-pn_gray-950 flex-1">
                        Phone
                      </span>
                      <Switcher
                        onChange={handleToggles}
                        checked={toggles.phone_information}
                        name="phone_information"
                        id="phone_information"
                      />
                    </div>

                    <div className="flex items-center gap-x-2">
                      <span className="text-pn-body-regular text-pn_gray-950 flex-1">
                        Letter
                      </span>
                      <Switcher
                        onChange={handleToggles}
                        checked={toggles.letter_information}
                        name="letter_information"
                        id="letter_information"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-y-[7px]">
                  <span className="text-pn-callout-emphasis text-pn_gray-950">
                    Marketing Information
                  </span>
                  <div className="flex flex-col gap-y-[14px]">
                    <div className="flex items-center gap-x-2">
                      <span className="text-pn-body-regular text-pn_gray-950 flex-1">
                        Email
                      </span>
                      <Switcher
                        onChange={handleToggles}
                        checked={toggles.email_marketing}
                        name="email_marketing"
                        id="email_marketing"
                      />
                    </div>

                    <div className="flex items-center gap-x-2">
                      <span className="text-pn-body-regular text-pn_gray-950 flex-1">
                        Phone
                      </span>
                      <Switcher
                        onChange={handleToggles}
                        checked={toggles.phone_marketing}
                        name="phone_marketing"
                        id="phone_marketing"
                      />
                    </div>

                    <div className="flex items-center gap-x-2">
                      <span className="text-pn-body-regular text-pn_gray-950 flex-1">
                        Letter
                      </span>
                      <Switcher
                        onChange={handleToggles}
                        checked={toggles.letter_marketing}
                        name="letter_marketing"
                        id="letter_marketing"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-y-[7px]">
                  <span className="text-pn-callout-emphasis text-pn_gray-950">
                    New Products and Services
                  </span>
                  <div className="flex flex-col gap-y-[14px]">
                    <div className="flex items-center gap-x-2">
                      <span className="text-pn-body-regular text-pn_gray-950 flex-1">
                        Email
                      </span>
                      <Switcher
                        onChange={handleToggles}
                        checked={toggles.email_products}
                        name="email_products"
                        id="email_products"
                      />
                    </div>

                    <div className="flex items-center gap-x-2">
                      <span className="text-pn-body-regular text-pn_gray-950 flex-1">
                        Phone
                      </span>
                      <Switcher
                        onChange={handleToggles}
                        checked={toggles.phone_products}
                        name="phone_products"
                        id="phone_products"
                      />
                    </div>

                    <div className="flex items-center gap-x-2">
                      <span className="text-pn-body-regular text-pn_gray-950 flex-1">
                        Letter
                      </span>
                      <Switcher
                        onChange={handleToggles}
                        checked={toggles.letter_products}
                        name="letter_products"
                        id="letter_products"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </Suspense>
  );
};
export default ProfileManage;
